import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";

import { api } from "./api";
import { Bar, Btn, Strip, money } from "./ui";

/**
 * Elegir el creativo de un anuncio, de dos fuentes distintas:
 *
 *  - BIBLIOTECA: los videos e imágenes que ya se usaron en anuncios, con su rendimiento al lado
 *    para poder cambiar lo que sugirió el generador con criterio.
 *  - PUBLICACIONES: posts de la página. Un anuncio hecho sobre un post existente conserva sus
 *    likes, comentarios y compartidos; subir el mismo video como creativo nuevo arranca en cero.
 *    Sólo se listan las que Meta marca como promocionables.
 */
export default function SelectorCreativo({ abierto, creativos, onElegir, onCerrar }) {
  const [fuente, setFuente] = useState("biblioteca");

  const [busqueda, setBusqueda] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [soloConHistoria, setSoloConHistoria] = useState(false);

  const [posts, setPosts] = useState(null);
  const [postsMeta, setPostsMeta] = useState(null);
  const [buscandoPosts, setBuscandoPosts] = useState(false);
  const [errorPosts, setErrorPosts] = useState(null);
  const [qPosts, setQPosts] = useState("");

  // Las publicaciones se traen recién cuando se pide la pestaña: son varias páginas de Graph.
  useEffect(() => {
    if (fuente !== "publicaciones" || posts !== null) return;
    buscarPosts("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fuente]);

  function buscarPosts(q) {
    setBuscandoPosts(true);
    setErrorPosts(null);
    api
      .publicaciones(q)
      .then((r) => {
        setPosts(r.publicaciones);
        setPostsMeta({ revisadas: r.total_revisadas, truncado: r.truncado });
      })
      .catch((e) => setErrorPosts(e.message))
      .finally(() => setBuscandoPosts(false));
  }

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (creativos || [])
      .filter((c) => (tipo === "todos" ? true : c.tipo === tipo))
      .filter((c) => (soloConHistoria ? !c.sin_historia : true))
      .filter((c) => (q ? (c.nombre || "").toLowerCase().includes(q) || c.clave.includes(q) : true))
      .slice(0, 300);
  }, [creativos, busqueda, tipo, soloConHistoria]);

  const conHistoria = (creativos || []).filter((c) => !c.sin_historia).length;

  return (
    <Dialog open={!!abierto} onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogContent className="ma-root" style={{ padding: 12 }}>
        <Bar style={{ marginBottom: 8 }}>
          <b style={{ fontSize: 13 }}>Elegir creativo</b>
          <Btn on={fuente === "biblioteca"} onClick={() => setFuente("biblioteca")}>biblioteca</Btn>
          <Btn on={fuente === "publicaciones"} onClick={() => setFuente("publicaciones")}>publicaciones</Btn>
          <div style={{ flex: 1 }} />
          <Btn onClick={onCerrar}>cerrar</Btn>
        </Bar>

        {/* ---------------- biblioteca ---------------- */}
        {fuente === "biblioteca" && (
          <>
            <Bar style={{ marginBottom: 6 }}>
              <TextField
                placeholder="buscar por nombre o id"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                style={{ flex: 1, minWidth: 180 }}
              />
              {["todos", "video", "imagen"].map((t) => (
                <Btn key={t} on={tipo === t} onClick={() => setTipo(t)}>{t}</Btn>
              ))}
              <Btn on={soloConHistoria} onClick={() => setSoloConHistoria(!soloConHistoria)}>con datos</Btn>
            </Bar>

            <div style={{ fontSize: 11.5, color: "#7A8699", marginBottom: 6 }}>
              {(creativos || []).length} creativos, {conHistoria} con gasto medido. Agrupados por
              video/imagen, así que las copias del mismo creativo aparecen una sola vez.
            </div>

            <div style={{ maxHeight: "58vh", overflowY: "auto", border: "1px solid #E3E8F0", borderRadius: 6 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>creativo</th>
                    <th>tipo</th>
                    <th style={{ textAlign: "right" }}>gasto</th>
                    <th style={{ textAlign: "right" }}>cmp</th>
                    <th style={{ textAlign: "right" }}>ROAS</th>
                    <th style={{ textAlign: "right" }}>CPA</th>
                    <th style={{ textAlign: "right" }}>CTR</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((c) => (
                    <tr key={c.clave}>
                      <td style={{ maxWidth: 300 }}>
                        <div style={{ fontWeight: 600 }}>{c.nombre}</div>
                        <div style={{ fontSize: 10.5, color: "#A0AABA" }}>{c.clave}</div>
                      </td>
                      <td>{c.tipo}</td>
                      <td style={{ textAlign: "right" }}>{c.sin_historia ? "—" : money(c.gasto)}</td>
                      <td style={{ textAlign: "right" }}>{c.sin_historia ? "—" : c.compras}</td>
                      <td
                        style={{
                          textAlign: "right", fontWeight: 800,
                          color: c.sin_historia ? "#A0AABA" : c.roas >= 1.5 ? "#16A34A" : c.roas < 0.9 ? "#D92D20" : "#24303F",
                        }}
                      >
                        {c.sin_historia ? "—" : Number(c.roas).toFixed(2)}
                      </td>
                      <td style={{ textAlign: "right" }}>{c.cpa == null ? "—" : money(c.cpa)}</td>
                      <td style={{ textAlign: "right" }}>{c.sin_historia ? "—" : `${Number(c.ctr).toFixed(2)}%`}</td>
                      <td style={{ textAlign: "right" }}>
                        <Btn tone="pri" onClick={() => onElegir(c)}>usar</Btn>
                      </td>
                    </tr>
                  ))}
                  {filtrados.length === 0 && (
                    <tr><td colSpan={8} style={{ color: "#A0AABA" }}>Nada coincide con la búsqueda.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ---------------- publicaciones ---------------- */}
        {fuente === "publicaciones" && (
          <>
            <Bar style={{ marginBottom: 6 }}>
              <TextField
                placeholder="buscar en el texto de la publicación"
                value={qPosts}
                onChange={(e) => setQPosts(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscarPosts(qPosts)}
                style={{ flex: 1, minWidth: 200 }}
              />
              <Btn tone="pri" onClick={() => buscarPosts(qPosts)} disabled={buscandoPosts}>
                {buscandoPosts ? "buscando…" : "buscar"}
              </Btn>
              {qPosts && (
                <Btn onClick={() => { setQPosts(""); buscarPosts(""); }} disabled={buscandoPosts}>limpiar</Btn>
              )}
            </Bar>

            <Strip tone="info" style={{ marginBottom: 8 }}>
              El anuncio se arma sobre la publicación: conserva sus likes y comentarios, y usa su
              propio texto (el copy del editor no se aplica). Se le agregan los utm igual, así que
              el tráfico queda atribuido.
            </Strip>

            {errorPosts && <Strip tone="error">{errorPosts}</Strip>}
            {buscandoPosts && !posts && <CircularProgress size={22} />}

            {postsMeta && (
              <div style={{ fontSize: 11.5, color: "#7A8699", marginBottom: 6 }}>
                {(posts || []).length} promocionables sobre {postsMeta.revisadas} revisadas
                {postsMeta.truncado ? " · hay más: acotá la búsqueda" : ""}
              </div>
            )}

            <div style={{ maxHeight: "50vh", overflowY: "auto", border: "1px solid #E3E8F0", borderRadius: 6 }}>
              {(posts || []).map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: "flex", gap: 10, alignItems: "center",
                    padding: 8, borderBottom: "1px solid #EEF1F6",
                  }}
                >
                  {p.imagen ? (
                    <img
                      src={p.imagen}
                      alt=""
                      style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 4, flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 54, height: 54, background: "#EEF1F6", borderRadius: 4, flexShrink: 0 }} />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, lineHeight: 1.35 }}>{p.resumen || "(sin texto)"}</div>
                    <div style={{ fontSize: 10.5, color: "#A0AABA" }}>
                      {p.fecha} · {p.tipo}
                      {p.enlace && (
                        <>
                          {" · "}
                          <a href={p.enlace} target="_blank" rel="noopener noreferrer" style={{ color: "#2B6CB0" }}>
                            ver en Facebook
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  <Btn tone="pri" onClick={() => onElegir({ clave: p.clave, nombre: p.resumen || p.id, tipo: "publicación" })}>
                    usar
                  </Btn>
                </div>
              ))}

              {posts && posts.length === 0 && !buscandoPosts && (
                <div style={{ padding: 10, color: "#A0AABA", fontSize: 12.5 }}>
                  No hay publicaciones promocionables que coincidan.
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
